import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FiMail, FiLock, FiHash } from 'react-icons/fi';

const AdminLogin = () => {
  const [formData, setFormData] = useState({ email: '', password: '', resellerId: '' });
  const [loading, setLoading] = useState(false);
  const [showResellerId, setShowResellerId] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    try {
      let captchaToken = null;
      if (executeRecaptcha) {
        captchaToken = await executeRecaptcha('admin_login');
      } else {
        toast.error('Captcha not ready. Please try again.');
        setLoading(false);
        return;
      }

      const result = await login(
        formData.email,
        formData.password,
        formData.resellerId || null,
        captchaToken
      );

      if (result?.success) {
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        toast.error(result?.message || 'Login failed.');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="w-full max-w-5xl flex flex-col lg:flex-row rounded-2xl overflow-hidden shadow-2xl">
        {/* Left panel */}
        <div className="lg:w-5/12 p-10 flex flex-col justify-between relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, rgb(130, 104, 178), #6a5294, #44366b)' }}>
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/5 rounded-full" />
          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-white/5 rounded-full" />

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <img src="/icon-192x192.png" alt="AIVAPOS Logo" className="w-7 h-7 rounded" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white leading-tight" style={{ fontFamily: '"Saved By Zero", Inter, sans-serif', letterSpacing: '0.05em' }}>AIVAPOS</h1>
                <p className="text-purple-200 text-xs">Management Platform</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-white leading-snug mb-3">
              Welcome back
            </h2>
            <p className="text-purple-200 text-sm leading-relaxed">
              Sign in to access your control panel and manage your business operations.
            </p>
          </div>

          <div className="relative z-10 mt-10 space-y-3">
            {[
              'Multi-tier reseller management',
              'Real-time POS & analytics',
              'Secure role-based access',
            ].map((f) => (
              <div key={f} className="flex items-center gap-2.5 text-purple-100 text-sm">
                <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                {f}
              </div>
            ))}
          </div>
        </div>

        {/* Right panel */}
        <div className="lg:w-7/12 bg-white p-10 flex flex-col justify-center">
          <div className="max-w-sm mx-auto w-full">
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900">Sign in to your account</h3>
              <p className="text-sm text-gray-500 mt-1">Enter your credentials below</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Email</label>
                <div className="relative">
                  <FiMail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                    className="h-10 w-full pl-9 pr-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Password</label>
                  <Link to="/forgot-password" className="text-xs text-purple-600 hover:text-purple-700 font-medium">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <FiLock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className="h-10 w-full pl-9 pr-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              {/* Reseller ID toggle */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowResellerId(!showResellerId)}
                  className="text-xs text-gray-400 hover:text-purple-600 flex items-center gap-1 transition-colors"
                >
                  <FiHash size={12} />
                  {showResellerId ? 'Hide' : 'Sign in as reseller or customer'}
                </button>
                {showResellerId && (
                  <div className="mt-2 relative">
                    <FiHash size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="resellerId"
                      value={formData.resellerId}
                      onChange={handleChange}
                      placeholder="Reseller ID (optional)"
                      className="h-10 w-full pl-9 pr-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-colors"
                    />
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 mt-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in…
                  </>
                ) : 'Sign In'}
              </button>
            </form>

            <p className="mt-6 text-xs text-center text-gray-400">
              Need access?{' '}
              <a href="mailto:support@aivapos.com" className="text-purple-600 hover:underline">
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
